// components/TutorialModal.js
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const steps = (t) => [
  {
    icon: '🎉',
    color: '#1a365d',
    title: t('tutorial.step1_title'),
    desc:  t('tutorial.step1_desc'),
  },
  {
    icon: '📅',
    color: '#2c7a7b',
    title: t('tutorial.step2_title'),
    desc:  t('tutorial.step2_desc'),
    tip:   t('tutorial.step2_tip'),
  },
  {
    icon: '👥',
    color: '#2f855a',
    title: t('tutorial.step3_title'),
    desc:  t('tutorial.step3_desc'),
    tip:   t('tutorial.step3_tip'),
  },
  {
    icon: '🪑',
    color: '#6b46c1',
    title: t('tutorial.seating_title'),
    desc:  t('tutorial.seating_desc'),
    tip:   t('tutorial.seating_tip'),
  },
  {
    icon: '🛠️',
    color: '#c53030',
    title: t('tutorial.step4_title'),
    desc:  t('tutorial.step4_desc'),
    tip:   t('tutorial.step4_tip'),
  },
  {
    icon: '📊',
    color: '#2b6cb0',
    title: t('tutorial.step5_title'),
    desc:  t('tutorial.step5_desc'),
    tip:   t('tutorial.step5_tip'),
  },
  {
    icon: '🤝',
    color: '#c05621',
    title: t('tutorial.step6_title'),
    desc:  t('tutorial.step6_desc'),
    tip:   t('tutorial.step6_tip'),
  },
  {
    icon: '🚀',
    color: '#1a365d',
    title: t('tutorial.step7_title'),
    desc:  t('tutorial.step7_desc'),
  },
];

export default function TutorialModal({ onDone }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [exiting, setExiting] = useState(false);

  const allSteps = steps(t);
  const step = allSteps[current];
  const isLast = current === allSteps.length - 1;

  const goTo = (idx) => {
    setExiting(true);
    setTimeout(() => { setCurrent(idx); setExiting(false); }, 200);
  };

  const next = () => isLast ? onDone() : goTo(current + 1);
  const back = () => goTo(current - 1);

  return (
    <div
      className="modal show d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', zIndex: 9999 }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 480 }}>
        <div
          className="modal-content border-0 shadow-xl overflow-hidden"
          style={{ borderRadius: 20 }}
        >
          {/* Coloured header band */}
          <div
            style={{
              background: step.color,
              padding: '2rem 1.5rem 1.5rem',
              textAlign: 'center',
              transition: 'background 0.3s ease',
            }}
          >
            <div style={{ fontSize: '3.5rem', lineHeight: 1, marginBottom: '0.5rem' }}>
              {step.icon}
            </div>
            <h4 className="text-white fw-bold mb-0">{step.title}</h4>
          </div>

          {/* Body */}
          <div
            className="modal-body px-4 pt-4 pb-3 text-center"
            style={{
              opacity: exiting ? 0 : 1,
              transform: exiting ? 'translateY(8px)' : 'translateY(0)',
              transition: 'opacity 0.2s ease, transform 0.2s ease',
              minHeight: 140,
            }}
          >
            <p className="text-muted mb-3" style={{ lineHeight: 1.7 }}>{step.desc}</p>
            {step.tip && (
              <div
                className="rounded-3 px-3 py-2 text-start small"
                style={{ background: `${step.color}15`, borderLeft: `3px solid ${step.color}` }}
              >
                <i className="fas fa-lightbulb me-2" style={{ color: step.color }}></i>
                {step.tip}
              </div>
            )}
          </div>

          {/* Progress dots */}
          <div className="d-flex justify-content-center gap-2 mb-3">
            {allSteps.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                style={{
                  width: i === current ? 20 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === current ? step.color : '#cbd5e0',
                  border: 'none',
                  padding: 0,
                  transition: 'all 0.25s ease',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>

          {/* Footer buttons */}
          <div className="modal-footer border-0 pt-0 px-4 pb-4 d-flex justify-content-between">
            <button
              className="btn btn-outline-secondary btn-sm rounded-pill px-3"
              onClick={onDone}
            >
              {t('tutorial.skip')}
            </button>
            <div className="d-flex gap-2">
              {current > 0 && (
                <button
                  className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                  onClick={back}
                >
                  <i className="fas fa-arrow-left me-1"></i>{t('tutorial.back')}
                </button>
              )}
              <button
                className="btn btn-sm rounded-pill px-4 text-white"
                style={{ background: step.color }}
                onClick={next}
              >
                {isLast ? (
                  <><i className="fas fa-rocket me-1"></i>{t('tutorial.start')}</>
                ) : (
                  <>{t('tutorial.next')}<i className="fas fa-arrow-right ms-1"></i></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
